import { useState, useEffect, useCallback } from 'react';
import { WinRadio } from './WinComponents';
import { Pencil, Trash2, X } from 'lucide-react';
import MoneyInput from './MoneyInput';
import DateInput from './DateInput';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PERSONNES = [
  { id: 'R',  label: 'Requérant' },
  { id: 'C',  label: 'Conjoint' },
  { id: 'L1', label: 'Lien 1' },
  { id: 'L2', label: 'Lien 2' },
];

const personneLabel = (id) => {
  const p = PERSONNES.find(x => x.id === id);
  return p ? p.label : (id || '');
};

const InfoFinModal = ({ isOpen, onClose, noseqdaj, sourceId, onConsolidate, hasConjoint, hasLien1, hasLien2, initialAnneeType }) => {
  const defaultPersonne = sourceId && sourceId !== 'F' ? sourceId : 'R';

  const personnesDisponibles = [
    { id: 'R',  label: 'Requérant' },
    ...(hasConjoint ? [{ id: 'C',  label: 'Conjoint' }] : []),
    ...(hasLien1    ? [{ id: 'L1', label: 'Lien 1' }]  : []),
    ...(hasLien2    ? [{ id: 'L2', label: 'Lien 2' }]  : []),
  ];

  const [lignes, setLignes] = useState([]);
  const [typesRevenus, setTypesRevenus] = useState([]);
  const [periodes, setPeriodes] = useState([]);
  const [editRow, setEditRow] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filtrePersonnes, setFiltrePersonnes] = useState({R: true, C: true, L1: true, L2: true});
  // Indique si l'utilisateur a réellement modifié les lignes infofin pendant cette ouverture.
  // Si false à la fermeture, on NE consolide PAS pour ne pas écraser les colonnes adda3XX
  // de la DAJ avec un recalcul potentiellement destructif (ex: legacy data sans infofin).
  const [dirty, setDirty] = useState(false);

  const currentYear = new Date().getFullYear();
  const [anneeType, setAnneeType] = useState(initialAnneeType === 'courante' ? 'courante' : 'precedente');
  const anneeRef = anneeType === 'precedente' ? String(currentYear - 1) : String(currentYear);

  const makeEmpty = (an, personne) => ({
    personne: personne || defaultPersonne,
    adfitype: '', adfiper: 'Semaine', adfirev: '',
    adfisemdeb: `${an}-01-01`, adfisemfin: `${an}-12-31`,
    adfinbper: '52', nbEnfants: 0
  });

  const [form, setForm] = useState(makeEmpty(currentYear - 1, defaultPersonne));
  const [confirmDelete, setConfirmDelete] = useState({ isOpen: false, rowId: null });

  const loadLookups = useCallback(async () => {
    try {
      const [resT, resP] = await Promise.all([
        fetch(`${API_URL}/api/lookup/revsourc`),
        fetch(`${API_URL}/api/lookup/revperio`)
      ]);
      setTypesRevenus(await resT.json());
      setPeriodes(await resP.json());
    } catch (e) { console.error(e); }
  }, []);

  const loadLignes = useCallback(async () => {
    if (!noseqdaj) return;
    try {
      const res = await fetch(`${API_URL}/api/infofin/${noseqdaj}`);
      setLignes(await res.json());
    } catch (e) { console.error(e); }
  }, [noseqdaj]);

  useEffect(() => {
    if (isOpen) {
      // Synchroniser l'année de référence du modal sur celle de l'onglet Revenus.
      const at = initialAnneeType === 'courante' ? 'courante' : 'precedente';
      const ar = at === 'courante' ? String(currentYear) : String(currentYear - 1);
      setAnneeType(at);
      loadLookups();
      loadLignes();
      setEditRow(null);
      setForm(makeEmpty(ar, defaultPersonne));
      setFiltrePersonnes({R: true, C: true, L1: true, L2: true});
      setDirty(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, loadLookups, loadLignes]);

  useEffect(() => {
    if (!editRow) {
      setForm(prev => ({...makeEmpty(anneeRef, defaultPersonne), personne: prev.personne}));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anneeRef]);

  const calcNbSemaines = async (deb, fin) => {
    if (!deb || !fin) return;
    try {
      const res = await fetch(`${API_URL}/api/infofin/calculer-nb-semaines`, {
        method: 'POST', headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({date_deb: deb, date_fin: fin})
      });
      const data = await res.json();
      setForm(prev => ({...prev, adfinbper: String(data.nb_semaines)}));
    } catch (e) { console.error(e); }
  };

  const handleDateChange = (field, value) => {
    const newForm = {...form, [field]: value};
    setForm(newForm);
    if (field === 'adfisemdeb' && newForm.adfisemfin) calcNbSemaines(value, newForm.adfisemfin);
    if (field === 'adfisemfin' && newForm.adfisemdeb) calcNbSemaines(newForm.adfisemdeb, value);
  };

  // Consolide immédiatement les lignes infofin vers les colonnes daj.adda3XX
  // pour la(les) année(s) présente(s). Idempotent — utilisable après chaque
  // ajout/édition/suppression OU à la fermeture du modal.
  const consoliderAnnees = async (anneesList) => {
    if (!noseqdaj) return;
    try {
      for (const an of anneesList) {
        if (!an) continue;
        const res = await fetch(`${API_URL}/api/infofin/consolider/${noseqdaj}?annee=${an}`, {method: 'POST'});
        if (!res.ok) {
          console.error(`Consolidation échouée pour ${an}: HTTP ${res.status}`);
          continue;
        }
        const data = await res.json();
        if (onConsolidate) onConsolidate(data, an);
      }
    } catch (e) { console.error('Erreur consolidation:', e); }
  };

  const handleSave = async () => {
    if (!form.adfitype) { alert('Sélectionner un type de revenu'); return; }
    if (!noseqdaj) { alert('Veuillez enregistrer le dossier avant d\'ajouter des informations financières.'); return; }
    setLoading(true);
    try {
      const payload = {
        ...form,
        sourceid: form.personne,
        noseqdaj,
        adfian: anneeRef,
        adfirev: parseFloat(form.adfirev) || 0,
        nbEnfants: parseInt(form.nbEnfants) || 0,
        adfinbper: form.adfinbper || '0'
      };
      if (editRow) {
        await fetch(`${API_URL}/api/infofin/${editRow}`, {
          method: 'PUT', headers: {'Content-Type': 'application/json'}, body: JSON.stringify(payload)
        });
      } else {
        await fetch(`${API_URL}/api/infofin`, {
          method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify(payload)
        });
      }
      setEditRow(null);
      setForm(makeEmpty(anneeRef, form.personne));
      setDirty(true);
      await loadLignes();
      // Consolidation immédiate : synchronise daj.adda3XX dès maintenant.
      // Ainsi l'utilisateur n'a pas besoin de fermer le modal pour voir le total à jour.
      await consoliderAnnees([anneeRef]);
    } catch (e) { console.error(e); alert('Erreur lors de la sauvegarde'); }
    setLoading(false);
  };

  const handleDelete = async (rowId) => {
    setConfirmDelete({ isOpen: true, rowId });
  };

  const confirmDeleteAction = async () => {
    const rowId = confirmDelete.rowId;
    const ligne = lignes.find(l => l.RowId === rowId);
    const annee = ligne?.adfian || anneeRef;
    setConfirmDelete({ isOpen: false, rowId: null });
    if (!rowId) return;
    try {
      await fetch(`${API_URL}/api/infofin/${rowId}`, {method: 'DELETE'});
      setDirty(true);
      await loadLignes();
      await consoliderAnnees([annee]);
    } catch (e) { console.error(e); }
  };

  const handleEdit = (ligne) => {
    setEditRow(ligne.RowId);
    setForm({
      personne: ligne.sourceid || 'R',
      adfitype: ligne.adfitype || '', adfiper: ligne.adfiper || 'Semaine',
      adfirev: ligne.adfirev || '', adfisemdeb: ligne.adfisemdeb || '',
      adfisemfin: ligne.adfisemfin || '', adfinbper: ligne.adfinbper || '',
      nbEnfants: ligne.nbEnfants || 0
    });
    if (ligne.adfian === String(currentYear)) setAnneeType('courante');
    else setAnneeType('precedente');
  };

  const handleClose = async () => {
    // Ne consolider qu'en cas de modification réelle (ajout, édition, suppression).
    // L'ouverture/fermeture sans changement NE DOIT PAS écraser les colonnes adda3XX
    // de la DAJ. Filet de sécurité: en cas d'années multiples présentes dans la grille,
    // re-consolider toutes les années (la consolidation immédiate post-save ne couvre
    // que l'année de référence courante).
    if (noseqdaj && dirty) {
      const anneesPresentes = [...new Set(lignes.map(l => l.adfian).filter(Boolean))];
      await consoliderAnnees(anneesPresentes);
    }
    onClose();
  };

  const fmtMoney = (v) => {
    const num = Number(v) || 0;
    if (num === 0) return '0.00 $';
    const parts = num.toFixed(2).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return parts.join('.') + ' $';
  };

  const fmtRevenu = (v) => {
    const num = Number(v) || 0;
    if (num === 0) return '';
    const parts = num.toFixed(2).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return parts.join('.') + ' $';
  };

  const parseRevenu = (v) => parseFloat(String(v).replace(/[^\d.-]/g, '')) || 0;

  const annees = [...new Set(lignes.map(l => l.adfian || ''))].sort();

  // Normalise sourceid (legacy data peut contenir '  R  ', 'r', '' etc.).
  // - vide / null / espaces → 'R' (défaut Requérant, cf. backend ligne 2844)
  // - trim + upper pour matcher les clés du filtre {R, C, L1, L2}
  const normSourceId = (v) => {
    const s = (v == null ? '' : String(v)).trim().toUpperCase();
    return s || 'R';
  };

  // Lignes filtrées selon les cases cochées.
  // Comportement attendu : si la case est décochée → la ligne disparaît.
  // Par défaut on filtre la ligne si la clé n'est pas explicitement true
  // (sécurité : un sourceid inconnu ne doit PAS contourner le filtre).
  const lignesFiltrees = lignes.filter(l => filtrePersonnes[normSourceId(l.sourceid)] === true);
  const lignesParAnnee = {};
  annees.forEach(a => {
    lignesParAnnee[a] = lignesFiltrees.filter(l => (l.adfian || '') === a);
  });

  const dateMin = `${anneeRef}-01-01`;
  const dateMax = `${anneeRef}-12-31`;

  if (!isOpen) return null;

  const selectStyle = {height: '28px', fontSize: '14px', padding: '2px 4px', lineHeight: '28px'};
  const inputStyle = {height: '28px', fontSize: '14px', padding: '2px 6px'};

  // Couleur par personne pour faciliter la lecture visuelle
  const personneColor = { R: '#e8f4e8', C: '#e8eef8', L1: '#fef9e7', L2: '#fde8e8' };

  return (
    <div data-testid="infofin-modal-overlay" style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000}}>
      <div style={{backgroundColor: '#f0f0f0', border: '2px solid #333', width: '96vw', maxWidth: '1500px', height: '88vh', display: 'flex', flexDirection: 'column'}} data-testid="infofin-modal">

        {/* Title bar */}
        <div style={{backgroundColor: '#003366', color: 'white', padding: '7px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0}}>
          <span style={{fontWeight: 'bold', fontSize: '16px'}}>INFORMATION FINANCIÈRE — REVENUS DÉTAILLÉS</span>
          <button onClick={handleClose} style={{background: 'none', border: 'none', color: 'white', fontSize: '20px', cursor: 'pointer', lineHeight: 1}} data-testid="infofin-btn-close">X</button>
        </div>

        <div style={{padding: '14px 16px', overflow: 'auto', flex: 1}}>

          {/* Ligne 1 : Année de référence + Filtre personnes */}
          <div style={{display: 'flex', gap: 10, marginBottom: 10, alignItems: 'stretch'}}>

            {/* Année de référence */}
            <fieldset className="win-groupbox compact" style={{margin: 0, flex: '0 0 auto'}}>
              <legend>Année de référence (pour ajout)</legend>
              <div style={{display: 'flex', alignItems: 'center', gap: 24}}>
                <div style={{display: 'flex', alignItems: 'center', gap: 6}}>
                  <label className="win-field-label" style={{marginBottom: 0, fontSize: '14px'}}>Année</label>
                  <input className="win-input" readOnly value={anneeRef} data-testid="infofin-annee-ref" style={{...inputStyle, width: '70px', backgroundColor: '#e8e8e8', fontWeight: 'bold', textAlign: 'center'}} />
                </div>
                <WinRadio name="infofin_annee_type" value="precedente" checked={anneeType === 'precedente'} onChange={() => setAnneeType('precedente')} label={`Précédente (${currentYear - 1})`} />
                <WinRadio name="infofin_annee_type" value="courante" checked={anneeType === 'courante'} onChange={() => setAnneeType('courante')} label={`Courante (${currentYear})`} />
              </div>
            </fieldset>

            {/* Filtre du tableau */}
            <fieldset className="win-groupbox compact" style={{margin: 0, flex: 1}}>
              <legend>Voir les revenus de</legend>
              <div style={{display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap'}}>
                {/* Case Tout */}
                <label style={{display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: '14px', fontWeight: 'bold'}}>
                  <input type="checkbox" data-testid="filtre-tout"
                    checked={personnesDisponibles.every(p => filtrePersonnes[p.id])}
                    onChange={e => {
                      const val = e.target.checked;
                      setFiltrePersonnes(prev => {
                        const next = {...prev};
                        personnesDisponibles.forEach(p => { next[p.id] = val; });
                        return next;
                      });
                    }}
                    style={{width: 15, height: 15}} />
                  Tout
                </label>
                <div style={{width: 1, height: 20, backgroundColor: '#ccc'}} />
                {/* Cases par personne disponible */}
                {personnesDisponibles.map(p => (
                  <label key={p.id} style={{display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: '14px'}}>
                    <input type="checkbox" data-testid={`filtre-${p.id.toLowerCase()}`}
                      checked={!!filtrePersonnes[p.id]}
                      onChange={() => setFiltrePersonnes(prev => ({...prev, [p.id]: !prev[p.id]}))}
                      style={{width: 15, height: 15, accentColor: p.id === 'R' ? '#2d7a2d' : p.id === 'C' ? '#1a4fa0' : p.id === 'L1' ? '#a07c00' : '#a01a1a'}} />
                    <span style={{color: p.id === 'R' ? '#2d7a2d' : p.id === 'C' ? '#1a4fa0' : p.id === 'L1' ? '#a07c00' : '#a01a1a', fontWeight: 'bold'}}>{p.label}</span>
                  </label>
                ))}
              </div>
            </fieldset>

          </div>

          {/* Formulaire ajout */}
          <fieldset className="win-groupbox compact" style={{margin: '0 0 10px 0'}}>
            <legend>{editRow ? 'Modifier la ligne' : 'Ajouter un revenu'}</legend>
            <div style={{display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap'}}>

              {/* Personne */}
              <div style={{display: 'flex', alignItems: 'center', gap: 4, flex: '0 0 205px'}}>
                <label className="win-field-label" style={{marginBottom: 0, fontSize: '13px', whiteSpace: 'nowrap'}}>Personne</label>
                <select className="win-input" value={form.personne} onChange={e => setForm({...form, personne: e.target.value})} data-testid="infofin-personne" style={{...selectStyle, flex: 1, minWidth: 0}}>
                  {personnesDisponibles.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                </select>
              </div>

              {/* Type */}
              <div style={{display: 'flex', alignItems: 'center', gap: 4, flex: '0 1 190px'}}>
                <label className="win-field-label" style={{marginBottom: 0, fontSize: '13px', whiteSpace: 'nowrap'}}>Type</label>
                <select className="win-input" value={form.adfitype}
                  onChange={e => {
                    const newType = e.target.value;
                    setForm(prev => ({...prev, adfitype: newType, nbEnfants: newType === 'Pens. alim. enfants' ? (prev.nbEnfants || 0) : 0}));
                  }}
                  data-testid="infofin-type" style={{...selectStyle, flex: 1, minWidth: 0}}>
                  <option value="">-- Type --</option>
                  {typesRevenus.map(t => <option key={t.id} value={t.description}>{t.description}</option>)}
                </select>
              </div>

              {/* Période */}
              <div style={{display: 'flex', alignItems: 'center', gap: 4, flex: '0 1 185px'}}>
                <label className="win-field-label" style={{marginBottom: 0, fontSize: '13px', whiteSpace: 'nowrap'}}>Période</label>
                <select className="win-input" value={form.adfiper} onChange={e => setForm({...form, adfiper: e.target.value})} data-testid="infofin-periode" style={{...selectStyle, flex: 1, minWidth: 0}}>
                  {periodes.map(p => <option key={p.id} value={p.description}>{p.description}</option>)}
                </select>
              </div>

              {/* Nb enfants - actif seulement pour Pens. alim. enfants */}
              <div style={{display: 'flex', alignItems: 'center', gap: 4}}>
                <label className="win-field-label" style={{marginBottom: 0, fontSize: '13px', whiteSpace: 'nowrap', color: form.adfitype === 'Pens. alim. enfants' ? undefined : '#aaa'}}>ENFANT</label>
                <input className="win-input" type="text" inputMode="numeric"
                  value={form.adfitype === 'Pens. alim. enfants' ? (form.nbEnfants || '') : ''}
                  disabled={form.adfitype !== 'Pens. alim. enfants'}
                  onChange={e => setForm({...form, nbEnfants: parseInt(e.target.value.replace(/\D/g,'')) || 0})}
                  data-testid="infofin-nb-enfants"
                  style={{...inputStyle, width: '42px', textAlign: 'center', backgroundColor: form.adfitype === 'Pens. alim. enfants' ? undefined : '#e8e8e8', color: form.adfitype === 'Pens. alim. enfants' ? undefined : '#aaa'}} />
              </div>

              {/* Revenu */}
              <div style={{display: 'flex', alignItems: 'center', gap: 4, flex: '0 1 130px'}}>
                <label className="win-field-label" style={{marginBottom: 0, fontSize: '13px', whiteSpace: 'nowrap'}}>Revenu</label>
                <MoneyInput
                  value={parseFloat(form.adfirev) || 0}
                  onChange={v => setForm(prev => ({...prev, adfirev: v}))}
                  testId="infofin-montant"
                  style={{...inputStyle, flex: 1}} />
              </div>

              {/* Dates */}
              <div style={{display: 'flex', alignItems: 'center', gap: 4}}>
                <label className="win-field-label" style={{marginBottom: 0, fontSize: '13px', whiteSpace: 'nowrap'}}>Début</label>
                <DateInput value={form.adfisemdeb} onChange={v => handleDateChange('adfisemdeb', v)} min={dateMin} max={dateMax} testId="infofin-date-deb" style={{ width: 135 }} />
              </div>
              <div style={{display: 'flex', alignItems: 'center', gap: 4}}>
                <label className="win-field-label" style={{marginBottom: 0, fontSize: '13px', whiteSpace: 'nowrap'}}>Fin</label>
                <DateInput value={form.adfisemfin} onChange={v => handleDateChange('adfisemfin', v)} min={dateMin} max={dateMax} testId="infofin-date-fin" style={{ width: 135 }} />
              </div>

              {/* Nb semaines */}
              <div style={{display: 'flex', alignItems: 'center', gap: 3}}>
                <span style={{fontSize: '13px', color: '#555', whiteSpace: 'nowrap'}}>Sem.</span>
                <input className="win-input" readOnly value={form.adfinbper || '—'} data-testid="infofin-nbper" style={{...inputStyle, width: '38px', backgroundColor: '#e8e8e8', textAlign: 'center', fontWeight: 'bold'}} />
              </div>

              {/* Boutons */}
              <button className="win-btn" onClick={handleSave} disabled={loading} data-testid="infofin-btn-save" style={{padding: '4px 14px', fontWeight: 'bold', fontSize: '14px'}} title={editRow ? 'Modifier' : 'Ajouter'}>
                {editRow ? <Pencil size={14} color="#0066cc" /> : 'AJOUTER'}
              </button>
              {editRow && (
                <button className="win-btn" onClick={() => { setEditRow(null); setForm(makeEmpty(anneeRef, form.personne)); }} style={{padding: '4px 8px', display: 'flex', alignItems: 'center'}} title="Annuler"><X size={14} /></button>
              )}
            </div>
          </fieldset>

          {/* Grille des lignes groupées par année */}
          <fieldset className="win-groupbox compact" style={{margin: 0, flex: 1}}>
            <legend>Lignes de revenus ({lignesFiltrees.length}/{lignes.length})</legend>
            <div style={{maxHeight: '100%', overflow: 'auto'}}>
              <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '14px'}} data-testid="infofin-table">
                <thead>
                  <tr style={{backgroundColor: '#003366', color: 'white', position: 'sticky', top: 0, zIndex: 1}}>
                    <th style={{padding: '5px 8px', textAlign: 'left'}}>Année</th>
                    <th style={{padding: '5px 8px', textAlign: 'left'}}>Personne</th>
                    <th style={{padding: '5px 8px', textAlign: 'left'}}>Type</th>
                    <th style={{padding: '5px 8px', textAlign: 'left'}}>Période</th>
                    <th style={{padding: '5px 8px', textAlign: 'right'}}>Enfant</th>
                    <th style={{padding: '5px 8px', textAlign: 'right'}}>Revenu</th>
                    <th style={{padding: '5px 8px', textAlign: 'center'}}>Début</th>
                    <th style={{padding: '5px 8px', textAlign: 'center'}}>Fin</th>
                    <th style={{padding: '5px 8px', textAlign: 'right'}}>Sem.</th>
                    <th style={{padding: '5px 8px', textAlign: 'right'}}>Annuel</th>
                    <th style={{padding: '5px 8px', textAlign: 'center', width: '100px'}}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {lignesFiltrees.length === 0 ? (
                    <tr><td colSpan={11} style={{padding: '20px', textAlign: 'center', color: '#888'}}>{lignes.length === 0 ? 'Aucune ligne de revenu' : 'Aucune ligne pour la sélection active'}</td></tr>
                  ) : annees.map(an => {
                    const rows = lignesParAnnee[an] || [];
                    if (rows.length === 0) return null;
                    const sousTotal = rows.reduce((s, l) => s + (Number(l.annuel) || 0), 0);
                    return rows.map((l, i) => {
                      const sid = normSourceId(l.sourceid);
                      const bgColor = personneColor[sid] || (i % 2 === 0 ? '#fff' : '#f5f5f5');
                      return (
                        <tr key={l.RowId || `${an}-${i}`}>
                          <td style={{padding: '4px 8px', backgroundColor: bgColor}}>{i === 0 ? an : ''}</td>
                          <td style={{padding: '4px 8px', backgroundColor: bgColor, fontWeight: 'bold', fontSize: '13px'}}>{personneLabel(sid)}</td>
                          <td style={{padding: '4px 8px', backgroundColor: bgColor}}>{l.adfitype}</td>
                          <td style={{padding: '4px 8px', backgroundColor: bgColor}}>{l.adfiper}</td>
                          <td style={{padding: '4px 8px', textAlign: 'right', backgroundColor: bgColor}}>{l.nbEnfants || ''}</td>
                          <td style={{padding: '4px 8px', textAlign: 'right', backgroundColor: bgColor}}>{fmtMoney(l.adfirev)}</td>
                          <td style={{padding: '4px 8px', textAlign: 'center', backgroundColor: bgColor}}>{l.adfisemdeb}</td>
                          <td style={{padding: '4px 8px', textAlign: 'center', backgroundColor: bgColor}}>{l.adfisemfin}</td>
                          <td style={{padding: '4px 8px', textAlign: 'right', backgroundColor: bgColor}}>{l.adfinbper}</td>
                          <td style={{padding: '4px 8px', textAlign: 'right', fontWeight: 'bold', backgroundColor: bgColor}}>{fmtMoney(l.annuel)}</td>
                          <td style={{padding: '4px 8px', textAlign: 'center', backgroundColor: bgColor, whiteSpace: 'nowrap'}}>
                            <button className="win-btn" onClick={() => handleEdit(l)} style={{padding: '2px 6px', marginRight: 4, display: 'inline-flex', alignItems: 'center'}} data-testid={`infofin-edit-${an}-${i}`} title="Modifier"><Pencil size={13} color="#0066cc" /></button>
                            <button className="win-btn" onClick={() => handleDelete(l.RowId)} style={{padding: '2px 6px', display: 'inline-flex', alignItems: 'center', color: '#c00'}} data-testid={`infofin-del-${an}-${i}`} title="Supprimer"><Trash2 size={13} /></button>
                          </td>
                        </tr>
                      );
                    }).concat(
                      <tr key={`total-${an}`} style={{backgroundColor: '#d9e6f2', borderTop: '1px solid #666', borderBottom: '2px solid #999'}}>
                        <td style={{padding: '5px 8px', fontWeight: 'bold'}}>{an}</td>
                        <td colSpan={8} style={{padding: '5px 8px', fontWeight: 'bold', textAlign: 'right'}}>Total annualisé {an} :</td>
                        <td style={{padding: '5px 8px', textAlign: 'right', fontWeight: 'bold', fontSize: '15px'}}>{fmtMoney(sousTotal)}</td>
                        <td></td>
                      </tr>
                    );
                  }).flat()}
                </tbody>
              </table>
            </div>
          </fieldset>
        </div>
      </div>

      {/* Modal de confirmation de suppression */}
      {confirmDelete.isOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 3000
        }}>
          <div style={{
            backgroundColor: '#f0f0f0',
            border: '2px solid #333',
            borderRadius: 0,
            width: '400px',
            boxShadow: '4px 4px 10px rgba(0,0,0,0.3)'
          }}>
            <div style={{
              backgroundColor: '#c03030',
              color: 'white',
              padding: '8px 14px',
              fontWeight: 'bold',
              fontSize: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{fontSize: '20px'}}>⚠️</span>
              ATTENTION
            </div>
            <div style={{padding: '24px'}}>
              <p style={{margin: '0 0 24px 0', fontSize: '16px', textAlign: 'center', color: '#333'}}>
                Voulez-vous réellement supprimer cette ligne de revenu?
              </p>
              <div style={{display: 'flex', justifyContent: 'center', gap: 20}}>
                <button 
                  className="win-btn" 
                  onClick={confirmDeleteAction}
                  style={{padding: '8px 24px', fontWeight: 'bold', fontSize: '15px', minWidth: '80px'}}
                >
                  Oui
                </button>
                <button 
                  className="win-btn" 
                  onClick={() => setConfirmDelete({ isOpen: false, rowId: null })}
                  style={{padding: '8px 24px', fontSize: '15px', minWidth: '80px'}}
                >
                  Non
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InfoFinModal;
